/* =========================================================================
   LAZ Skincare — "add to my routine" control

   Local-first: every click reads and writes localStorage through
   window.LazRoutine. No account required, and no request on page load.

   Built for the product page, and since also used on product cards (the
   complementary-pairs section). The card case differs in two ways, both
   driven by attributes the snippet renders:

     data-lock-variant="true"  the variant is whatever Liquid resolved, and
                               no form on the page is consulted. See VARIANT
                               TRACKING below — without this a card's control
                               reads the MAIN product's form.
     the destination nodes      simply absent in compact mode. Every function
                               that touches them already returns early when
                               they are missing, so nothing here special-cases
                               it.

   NOT A TOGGLE. There are two controls and exactly one is shown:

     button  — adds the selected variant, or switches to it when the product
               is already in under a different size or shade.
     anchor  — shown once the exact variant on screen is in: a link through to
               the routine page.

   Removing used to happen right here, which meant deleting something while
   looking at a single product, with no view of what the routine held or where
   the gap would fall. The routine page has that view and its own per-item
   remove, so removal lives there and this control never destroys anything.

   NAMING THE DESTINATION. An add lands in whichever routine the working copy
   is currently bound to, and leaving that implicit meant products arrived
   somewhere the shopper never chose and could not see. Three presentations,
   in order of preference:

     1. Member with saved routines — a select naming the destination and
        letting them change it without leaving the product page.
     2. Anyone whose routine has a name — "Adding to skincare routine
        ‘Morning’", with the name linking to the routine. Guests get this too:
        they can name a routine without an account, and the name is the useful
        thing to show, not whether an account exists.
     3. Member with nothing named and nothing saved — a plain line saying the
        routine lives in this browser.

   A guest with an unnamed routine sees none of it: there is exactly one
   routine and no name to report, so the line would be noise on every product
   page.

   The select's options come from a JSON tag the snippet renders straight out
   of the customer metafield (id and name only), so showing the picker costs
   nothing. Switching costs one request, because the chosen routine's items
   have to be fetched — but only when someone actually switches.

   THREE MEMBERSHIP STATES, NOT TWO. A product can be in the routine under a
   *different* variant than the one on screen. Treating that as "in" made the
   control a remove, so a shopper switching from the 60 ml to the 180 ml
   deleted the product instead of changing size.

   VARIANT TRACKING. On the product page the authoritative value is the product
   form's hidden id input, read fresh from the DOM at click time — never cached
   — because this theme re-renders the product form on every variant change,
   replacing that input. For the same reason the change listeners are bound to
   the document rather than the form: a listener on a node the theme swaps out
   stops firing, which leaves the label stale while clicks still work. The
   snippet is also rendered outside the product form (a custom Liquid section),
   so the form is resolved by falling back to the page's add-to-cart form.

   That fallback is precisely why data-lock-variant exists. "The page's
   add-to-cart form" is unambiguous on a product page and actively wrong
   anywhere else: a control under a complementary product card would resolve
   to the main product's form and store the main product's variant id against
   the card's product. Locked, the control uses the variant Liquid wrote into
   data-variant-gid, consults no form, and binds none of the variant-watching
   listeners — there is nothing on a card that can change its variant.
   ========================================================================= */

(function () {
  "use strict";

  const SELECTOR = "[data-lsc-routine-button]";
  const LOCAL = "__local";

  function init(root) {
    if (!window.LazRoutine || root.dataset.lscReady === "true") return;
    root.dataset.lscReady = "true";

    const R = window.LazRoutine;
    const button = root.querySelector("[data-routine-toggle]");
    const openLink = root.querySelector("[data-routine-open]");
    const label = root.querySelector("[data-routine-label]");
    const icon = root.querySelector("[data-routine-icon]");
    const note = root.querySelector("[data-routine-note]");
    const target = root.querySelector("[data-routine-target]");
    const targetLabel = root.querySelector("[data-routine-target-label]");
    const select = root.querySelector("[data-routine-target-select]");
    const staticTarget = root.querySelector("[data-routine-target-static]");
    if (!button || !label) return;

    const isCustomer = root.dataset.customer === "true";
    const productGid = root.dataset.productGid || "";
    const handle = root.dataset.handle || "";
    const step = root.dataset.step || "";
    const routineUrl = root.dataset.routineUrl || "/pages/routine";

    /* LSC - Set on a card, where no form on this page describes this product. */
    const lockVariant = root.dataset.lockVariant === "true";

    let actives = [];
    try {
      const parsed = JSON.parse(root.dataset.actives || "[]");
      if (Array.isArray(parsed)) actives = parsed.map(String);
    } catch {
      actives = [];
    }

    /* Rendered by Liquid from the customer metafield: no request. */
    let saved = [];
    try {
      const tag = root.querySelector("[data-routine-saved-list]");
      const parsed = tag ? JSON.parse(tag.textContent) : [];
      if (Array.isArray(parsed)) saved = parsed.filter((r) => r && r.id && r.name);
    } catch {
      saved = [];
    }

    /* Resolved fresh each time: the theme may have replaced the form. */
    function cartForm() {
      return (
        root.closest("form[action*='/cart/add']") ||
        document.querySelector("form[action*='/cart/add']")
      );
    }

    function currentVariantGid() {
      /* LSC - Locked: the variant Liquid resolved, full stop. Reading a form
         here is not a fallback, it is a different product. */
      if (lockVariant) return root.dataset.variantGid || "";

      const form = cartForm();
      const input = form && form.querySelector("input[name='id'], select[name='id']");
      const id = input && input.value && String(input.value).trim();
      if (id && /^\d+$/.test(id)) return `gid://shopify/ProductVariant/${id}`;
      return root.dataset.variantGid || "";
    }

    /* "this" — the exact variant on screen is stored.
       "other" — the product is stored under a different variant.
       "none" — not in the routine at all. */
    function membership() {
      const gid = currentVariantGid();
      if (gid && R.hasVariant(gid)) return "this";
      if (productGid && R.hasProduct(productGid)) return "other";
      return "none";
    }

    /* Only the two states the button itself can be in. "this" has no entry
       because the anchor takes over there, carrying its own fixed label. */
    const LABELS = {
      other: "Switch to this option",
      none: "Add to my routine",
    };

    /* The checklist mark, matching the header nav link, so the control reads
       as part of the routine feature rather than as a generic add button.

       It replaced a plain list of lines, which next to the header's hamburger
       read as a second menu button. The tick legs are drawn long on purpose:
       at this 2px stroke a shorter leg would be barely longer than the stroke
       is thick and would render as a blob rather than a tick. */
    const CHECKLIST_GLYPH =
      '<path d="M2.8 5.9l1.7 1.7 3.2-3.4M2.8 11.9l1.7 1.7 3.2-3.4M2.8 17.9l1.7 1.7 3.2-3.4"/>' +
      '<path d="M11 5.9h10M11 11.9h10M11 17.9h10"/>';

    /* 17.6 rather than 16: the checklist runs 10% larger than this control's
       other glyphs, matching the same bump given to the header copy. Keep this
       in step with the resting markup in snippets/lsc-routine-button.liquid —
       the two have to agree or the icon jumps size on the first re-render. */
    const GLYPH_SIZE = 17.6;

    const ICONS = {
      other: CHECKLIST_GLYPH,
      none: CHECKLIST_GLYPH,
    };

    function setIcon(state) {
      if (!icon) return;
      icon.innerHTML =
        '<svg viewBox="0 0 24 24" width="' + GLYPH_SIZE + '" height="' + GLYPH_SIZE + '" ' +
        'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
        'stroke-linejoin="round" aria-hidden="true">' +
        (ICONS[state] || CHECKLIST_GLYPH) +
        "</svg>";
    }

    /* ---------------- destination ---------------- */

    let lastSignature = null;

    const preposition = () => (membership() === "none" ? "Adding to" : "In");

    /* The first option is "somewhere other than a saved routine", and what
       that means depends on the working copy.

       Critically it must NEVER borrow the bound routine's name: while a saved
       routine is open, state.name IS that routine's name, so using it here
       rendered this option and the saved one with identical text — every
       entry in the list appeared to read as the active routine, and the
       duplicate only resolved after selecting it, because clearing wiped the
       name. When bound, this option means "start a new one", nothing else. */
    function localOptionLabel(state) {
      if (state.source_id) return "New routine";
      if (state.name) return state.name;
      return state.items.length ? "My unsaved routine" : "New routine";
    }

    /* "Adding to skincare routine ‘Morning’", the name linking to the routine.
       Built from nodes rather than markup because the name is shopper-written
       text and must never be parsed as HTML. */
    function renderNamedLine(name) {
      staticTarget.textContent = "";
      staticTarget.appendChild(
        document.createTextNode(`${preposition()} skincare routine `)
      );
      const link = R.el("a", "link", `“${name}”`);
      link.href = routineUrl;
      staticTarget.appendChild(link);
      staticTarget.hidden = false;
      target.hidden = true;
    }

    /* Returns early in compact mode, where neither node was rendered. */
    function renderTarget() {
      if (!target || !staticTarget) return;

      const state = R.read();

      /* A member with saved routines gets the picker — the only presentation
         that lets them change where an add goes. */
      if (isCustomer && saved.length) {
        staticTarget.hidden = true;
        target.hidden = false;

        const current = state.source_id || LOCAL;
        const signature = [
          current,
          localOptionLabel(state),
          saved.map((r) => r.id + r.name).join("|"),
        ].join("::");

        /* Rebuilding on every variant change would fight the shopper's
           cursor, so only touch the DOM when something actually differs. */
        if (signature !== lastSignature) {
          lastSignature = signature;
          select.textContent = "";

          const localOption = document.createElement("option");
          localOption.value = LOCAL;
          localOption.textContent = localOptionLabel(state);
          select.appendChild(localOption);

          for (const routine of saved) {
            const option = document.createElement("option");
            option.value = routine.id;
            option.textContent = routine.name;
            select.appendChild(option);
          }
        }

        select.value = current;
        if (select.value !== current) select.value = LOCAL; // deleted elsewhere
        if (targetLabel) targetLabel.textContent = preposition();
        return;
      }

      /* A name is worth showing whether or not there is an account behind it. */
      if (state.name) return renderNamedLine(state.name);

      if (isCustomer) {
        target.hidden = true;
        staticTarget.hidden = false;
        staticTarget.textContent = "Adding to your routine, saved in this browser";
        return;
      }

      /* Guest, unnamed: one routine, nothing to report. */
      target.hidden = true;
      staticTarget.hidden = true;
    }

    /* Switching destination replaces the working copy, so anything not yet on
       the account is about to go. Land it first; only ask if that fails. */
    async function switchTo(id) {
      const state = R.read();

      if (state.source_id && state.unsynced) {
        const flushed = await R.syncNow();
        if (!flushed) {
          const ok = window.confirm(
            `Changes to “${state.name || "your open routine"}” haven't reached your account yet. Switch anyway and discard them?`
          );
          if (!ok) return renderTarget();
        }
      } else if (!state.source_id && state.items.length) {
        const ok = window.confirm("Your unsaved routine will be replaced. Continue?");
        if (!ok) return renderTarget();
      }

      if (id === LOCAL) {
        R.clear();
        R.toast("Started a new routine.", "info");
        render();
        return;
      }

      select.disabled = true;
      try {
        /* The items were deliberately left out of the page payload, so this is
           where they get fetched — once, on an actual switch. */
        const res = await R.savedRoutines();
        const routine = (res.routines || []).find((r) => r.id === id);
        if (!routine) throw new Error("That routine no longer exists.");

        R.replace(routine.items || [], {
          source_id: routine.id,
          name: routine.name,
          note: routine.note,
        });
        R.toast(`Now adding to “${routine.name}”.`, "success");
      } catch (err) {
        R.toast(err.message, "error");
      } finally {
        select.disabled = false;
        render();
      }
    }

    if (select) {
      select.addEventListener("change", () => switchTo(select.value));
      /* A change here must not be mistaken for a variant change by the page's
         other listeners. */
      select.addEventListener("change", (e) => e.stopPropagation());
    }

    /* ---------------- the control ---------------- */

    function render() {
      const state = membership();
      const inRoutine = state === "this";

      /* Swap which of the two controls is on screen. */
      button.hidden = inRoutine;
      if (openLink) openLink.hidden = !inRoutine;

      if (!inRoutine) {
        label.textContent = LABELS[state];
        setIcon(state);
      }

      /* The small "View your routine" line under the control would repeat the
         link the control itself has become, so it only shows for "other". */
      if (note) note.hidden = state === "none" || inRoutine;

      renderTarget();
    }

    button.addEventListener("click", () => {
      const gid = currentVariantGid();

      if (!gid) {
        R.toast("Choose an option first.", "error");
        return;
      }

      const state = membership();
      const routine = R.read();
      const where = routine.name ? ` “${routine.name}”` : " your routine";

      /* add() replaces any other variant of the same product, so switching
         size or shade needs no separate remove. Nothing here deletes. */
      const ok = R.add({
        variant_gid: gid,
        product_gid: productGid,
        handle,
        step_label: step,
        actives,
      });

      if (!ok) {
        R.toast(
          "We couldn't save your routine — your browser is blocking storage.",
          "error"
        );
        return;
      }

      R.toast(state === "other" ? `Updated${where}.` : `Added to${where}.`, "success");
      render();
    });

    /* The engine writes bound routines through to the account on its own. Say
       something only when that fails — a silent failure here is exactly the
       class of bug this feature exists to prevent. */
    R.onSync((e) => {
      const detail = e.detail || {};
      if (detail.state !== "error") return;
      R.toast(
        detail.error && detail.error.status === 401
          ? "You've been logged out, so this wasn't saved to your account."
          : "Saved in this browser, but we couldn't reach your account.",
        "error"
      );
    });

    /* Re-render on anything that might have changed the selected variant.
       Bound to the document in capture phase so it keeps working after the
       theme swaps the form out, and repeated after a delay because the section
       re-render lands asynchronously.

       LSC - Skipped entirely when locked. A card has no variant picker, so
       these would fire on every click anywhere on the page to recompute a
       value that cannot have changed — and a page carrying several cards would
       bind the whole set once per card. R.onChange below still keeps a locked
       control in step with the routine itself. */
    let lastVariant = null;
    function refresh() {
      const gid = currentVariantGid();
      if (gid === lastVariant) return;
      lastVariant = gid;
      render();
    }

    function scheduleRefresh() {
      requestAnimationFrame(refresh);
      window.setTimeout(refresh, 250);
      window.setTimeout(refresh, 800);
    }

    if (!lockVariant) {
      document.addEventListener("change", scheduleRefresh, true);
      document.addEventListener("click", scheduleRefresh, true);
      ["variant:change", "variantChange", "on:variant:change"].forEach((name) => {
        document.addEventListener(name, scheduleRefresh);
      });
      document.addEventListener("shopify:section:load", scheduleRefresh);
    }

    /* Another tab, or the routine page in this one, may have changed things. */
    R.onChange(render);

    lastVariant = currentVariantGid();
    render();
  }

  function boot() {
    document.querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", boot);
})();
